/**
 * ChallengesPageContext - Page-specific context for challenges listing page
 * Provides all challenges page data, actions, and state.
 * History (finished challenges) is lazy-loaded: only fetched when the user
 * switches to the Finished tab or expands the Finished section in the All tab.
 */

import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react'
import { Platform } from 'react-native'
import { useRouter, useGlobalSearchParams } from 'expo-router'
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext'
import { useFocusEffect } from '@react-navigation/native'
import { useQueryClient } from '@tanstack/react-query'
import { USER_CONFIG } from '../../../constants'
import { useChallenges } from '../hooks/useChallenges'
import { useChallengeFilters } from '../hooks/useChallengeFilters'
import { useChallengesMatchData } from '../hooks/useChallengesMatchData'
import { useMenu } from '../hooks/useMenu'
import { resetChallenges } from '../helpers/challengesApi'
import { useApiClient } from '../../../helpers/apiClient.helper'
import { EXPAND_CHALLENGE_PARAM } from '../constants/challenges.constants'
import type { ChallengeWithStats } from '../../../types/challenges'
import type { ChallengeSectionBuckets, FilterType } from '../types/challenges.types'
import type { SnackbarVariant } from '../../shared/stores/snackbarStore'
import { useSnackbar } from '../../shared/hooks/useSnackbar'

interface ChallengesPageContextValue {
  challenges: ChallengeWithStats[]
  historyChallenges: ChallengeWithStats[]
  /** First 5 finished challenges, fetched in parallel with active on page load (no interaction required). */
  historyPreview: ChallengeWithStats[]
  isInitialLoadComplete: boolean
  isLoading: boolean
  isActiveError: boolean
  /** True when history query is running its initial fetch (no cached data yet). Skeleton-only signal. */
  isLoadingHistory: boolean
  /** True while the 5-item preview query is in flight. */
  isLoadingHistoryPreview: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  loadChallenges: () => Promise<void>
  refetchActive: () => Promise<unknown>
  resetHistory: () => void
  fetchMoreHistory: () => Promise<unknown>

  matchesMap: Record<string, unknown>

  activeFilter: FilterType
  setActiveFilter: (filter: FilterType) => void
  filteredChallenges: ChallengeWithStats[]
  sectionBuckets: ChallengeSectionBuckets

  /** Whether history data has been requested at least once (Finished tab opened). */
  historyRequested: boolean

  menuVisible: boolean
  setMenuVisible: (visible: boolean) => void
  handleMenuPress: () => void
  handleSignOut: () => Promise<void>

  handleResetChallenges: () => Promise<void>
  handleChallengePress: (challenge: ChallengeWithStats) => void
  handleShowMessage: (payload: { message: string; variant: SnackbarVariant }) => void
  currentUserId: string

  scrollTargetChallengeId: string | null
  expandedChallengeId: string | null
  setExpandedChallengeId: (id: string | null) => void
  clearExpandChallengeId: () => void
}

export const ChallengesPageContext = createContext<ChallengesPageContextValue | null>(
  null
)

interface ChallengesPageProviderProps {
  children: React.ReactNode
}

export const ChallengesPageProvider: React.FC<ChallengesPageProviderProps> = ({
  children,
}) => {
  const { isSignedIn, userId, isLoaded } = useSupabaseAuth()
  const currentUserId = userId || USER_CONFIG.defaultUserId
  const params = useGlobalSearchParams<{ [key: string]: string | string[] }>()
  const rawExpand = params[EXPAND_CHALLENGE_PARAM]
  const scrollTargetChallengeId = (() => {
    if (typeof rawExpand === 'string' && rawExpand.trim()) return rawExpand.trim()
    if (Array.isArray(rawExpand) && rawExpand[0] && typeof rawExpand[0] === 'string' && rawExpand[0].trim()) return rawExpand[0].trim()
    return null
  })()

  const [expandedChallengeId, setExpandedChallengeId] = useState<string | null>(null)

  // Lazy-load full history: only fetch when user opens the Finished tab
  const [historyRequested, setHistoryRequested] = useState(false)

  const router = useRouter()
  const queryClient = useQueryClient()
  const queryClientRef = React.useRef(queryClient)
  queryClientRef.current = queryClient
  const apiClient = useApiClient()
  const apiClientRef = React.useRef(apiClient)
  apiClientRef.current = apiClient

  // Do NOT auto-expand Finished or request history from the URL param on load.
  // Finished section and COMPLETED-challenges API only run when the user expands the section or taps the Finished tab.
  // scrollTargetChallengeId is still read from the URL so that when the user does open Finished, ChallengesList can scroll to that challenge if present.

  const {
    challenges: activeChallenges,
    historyChallenges,
    historyPreview,
    isInitialLoadComplete,
    isLoading,
    isActiveError,
    isLoadingHistory,
    isLoadingHistoryPreview,
    isFetchingNextPage,
    hasNextPage,
    loadChallenges,
    refetchActive,
    resetHistory,
    fetchMoreHistory,
  } = useChallenges({
    currentUserId,
    isSignedIn: isSignedIn === true,
    isLoaded,
    historyEnabled: historyRequested,
  })

  const challengesList = useMemo(() => activeChallenges ?? [], [activeChallenges])

  const challengesForMatchData = useMemo(
    () => [...challengesList, ...(historyChallenges ?? []), ...(historyPreview ?? [])],
    [challengesList, historyChallenges, historyPreview]
  )

  const { matchesMap } = useChallengesMatchData(challengesForMatchData)

  const { activeFilter, setActiveFilter: setActiveFilterRaw, filteredChallenges, sectionBuckets } =
    useChallengeFilters({
      challenges: challengesList,
      currentUserId,
    })

  // When switching to the Finished tab, also request history
  const setActiveFilter = useCallback((filter: FilterType) => {
    if (filter === 'finished') {
      setHistoryRequested(true)
    }
    setActiveFilterRaw(filter)
  }, [setActiveFilterRaw])

  const effectiveFilteredChallenges =
    activeFilter === 'finished' ? historyChallenges : filteredChallenges

  const { menuVisible, setMenuVisible, handleMenuPress, handleSignOut } =
    useMenu()
  const showSnackbar = useSnackbar().showSnackbar

  const handleShowMessage = useCallback(
    (payload: { message: string; variant: SnackbarVariant }) => {
      showSnackbar(payload.message, payload.variant)
    },
    [showSnackbar]
  )

  const loadChallengesRef = React.useRef(loadChallenges)
  useEffect(() => {
    loadChallengesRef.current = loadChallenges
  }, [loadChallenges])

  const lastLoadTimeRef = React.useRef<number>(0)
  useFocusEffect(
    useCallback(() => {
      if (!isSignedIn || !isLoaded) return
      const now = Date.now()
      const activeState = queryClientRef.current.getQueryState([
        'challenges',
        'active',
        currentUserId,
      ])
      const previewState = queryClientRef.current.getQueryState([
        'challenges',
        'history-preview',
        currentUserId,
      ])
      if (Platform.OS === 'web' && activeState?.fetchStatus === 'fetching') {
        return
      }
      const activeFresh =
        typeof activeState?.dataUpdatedAt === 'number' &&
        now - activeState.dataUpdatedAt <= 30000
      const previewFresh =
        typeof previewState?.dataUpdatedAt === 'number' &&
        now - previewState.dataUpdatedAt <= 30000
      const recentlyRefetched = activeFresh && previewFresh
      if (recentlyRefetched) return
      if (now - lastLoadTimeRef.current <= 30000) return
      lastLoadTimeRef.current = now
      loadChallengesRef.current()
    }, [isSignedIn, isLoaded, currentUserId])
  )

  const handleResetChallenges = useCallback(async () => {
    const success = await resetChallenges(apiClientRef.current)
    if (success) {
      loadChallenges()
    }
  }, [loadChallenges])

  const clearExpandChallengeId = useCallback(() => {
    router.replace('/app/arena' as never)
  }, [router])
  const setExpandedChallengeIdStable = useCallback((id: string | null) => {
    setExpandedChallengeId(id)
  }, [])

  const handleChallengePress = useCallback(
    (challenge: ChallengeWithStats) => {
      router.push(`/app/challenge/${challenge.id}`)
    },
    [router]
  )

  const value: ChallengesPageContextValue = useMemo(
    () => ({
      challenges: challengesList,
      historyChallenges,
      historyPreview,
      isInitialLoadComplete,
      isLoading,
      isActiveError,
      isLoadingHistory,
      isLoadingHistoryPreview,
      isFetchingNextPage,
      hasNextPage,
      loadChallenges,
      refetchActive,
      resetHistory,
      fetchMoreHistory,
      matchesMap,
      currentUserId,
      activeFilter,
      setActiveFilter,
      filteredChallenges: effectiveFilteredChallenges,
      sectionBuckets,
      historyRequested,
      menuVisible,
      setMenuVisible,
      handleMenuPress,
      handleSignOut,
      handleResetChallenges,
      handleChallengePress,
      handleShowMessage,
      scrollTargetChallengeId,
      expandedChallengeId,
      setExpandedChallengeId: setExpandedChallengeIdStable,
      clearExpandChallengeId,
    }),
    [
      challengesList,
      historyChallenges,
      historyPreview,
      isInitialLoadComplete,
      isLoading,
      isActiveError,
      isLoadingHistory,
      isLoadingHistoryPreview,
      isFetchingNextPage,
      hasNextPage,
      loadChallenges,
      refetchActive,
      resetHistory,
      fetchMoreHistory,
      matchesMap,
      currentUserId,
      activeFilter,
      setActiveFilter,
      effectiveFilteredChallenges,
      sectionBuckets,
      historyRequested,
      menuVisible,
      setMenuVisible,
      handleMenuPress,
      handleSignOut,
      handleResetChallenges,
      handleChallengePress,
      handleShowMessage,
      scrollTargetChallengeId,
      expandedChallengeId,
      setExpandedChallengeIdStable,
      clearExpandChallengeId,
    ]
  )

  return (
    <ChallengesPageContext.Provider value={value}>
      {children}
    </ChallengesPageContext.Provider>
  )
}

export const useChallengesPage = (): ChallengesPageContextValue => {
  const context = useContext(ChallengesPageContext)
  if (!context) {
    throw new Error(
      'useChallengesPage must be used within ChallengesPageProvider'
    )
  }
  return context
}
